import { Action } from 'redux';
import * as _ from 'underscore';

import { Tableau } from '../../common/utilities/TableauLog';
import { isSetNavLocationAction } from '../actions/spaces/SetNavLocationAction';
import { NavLocation, NavLocationAreas } from '../layout/NavLocation';

const InitialState: NavLocation = NavLocationAreas.Generic.unknown;

export function navLocationReducer(
  previousState: NavLocation = InitialState,
  action: Action
): NavLocation  {
  if (isSetNavLocationAction(action)) {
    const matchingStateName =
      _.find(routerStateNameToNavLocationKeys, (stateName) => action.routerStateName.startsWith(stateName));

    if (!matchingStateName) {
      Tableau.Logger().error('Unexpected state: ' + action.routerStateName);
      return NavLocationAreas.Generic.unknown;
    }

    const newNavLocation = routerStateNameToNavLocation[matchingStateName];

    if (newNavLocation !== previousState) {
      return newNavLocation;
    }
  }

  return previousState;
}

const routerStateNameToNavLocation: _.Dictionary<NavLocation> = {
  // Because most bad routes happen in regards to content items (e.g. invalid workbook ID),
  // we treat the "error" state as part of the site area. This will ensure that nav elements
  // (including the alerts menu and site picker) stay in "site mode" if the user lands on an error page.
  'main.error': NavLocationAreas.SiteContent.site_unspecified,

  // more specific paths have to come before their superclasses (e.g., 'favorites' could match 'content', so put them first)
  'main.site.content.favorites': NavLocationAreas.SiteContent.favorites,
  'main.site.content.recents': NavLocationAreas.SiteContent.recents,
  'main.site.content.recommendations': NavLocationAreas.SiteContent.recommendations,
  'main.site.content.datasource.askDataUsageAnalytics': NavLocationAreas.SiteContent.datasource_owner_panel,
  // specific for search so that search space has none left-nav link selected
  'main.site.content.search': NavLocationAreas.SiteContent.search,
  'main.site.content': NavLocationAreas.SiteContent.explore,
  'main.site.home': NavLocationAreas.SiteContent.home,
  'main.site.catalog': NavLocationAreas.SiteContent.catalog,
  'main.site.viz': NavLocationAreas.SiteContent.site_viz,
  'main.site.sandbox': NavLocationAreas.SiteContent.sandbox,
  'main.site.metrics': NavLocationAreas.SiteContent.metric,

  'main.site.group': NavLocationAreas.SiteAdmin.site_groups,
  'main.site.groups': NavLocationAreas.SiteAdmin.site_groups,
  'main.site.backgroundJobs': NavLocationAreas.SiteAdmin.site_jobs,
  'main.site.analysis': NavLocationAreas.SiteAdmin.site_viz,  // status vizzes
  'main.site.schedule': NavLocationAreas.SiteAdmin.site_schedules,
  'main.site.schedules': NavLocationAreas.SiteAdmin.site_schedules,
  'main.site.settings': NavLocationAreas.SiteAdmin.site_settings,
  'main.site.status': NavLocationAreas.SiteAdmin.site_status,
  'main.site.tasks': NavLocationAreas.SiteAdmin.site_tasks,
  'main.site.user': NavLocationAreas.SiteAdmin.site_users,
  'main.site.users': NavLocationAreas.SiteAdmin.site_users,

  'main.site': NavLocationAreas.SiteContent.site_unspecified,

  // server pages
  'main.server.analysis': NavLocationAreas.ServerAdmin.server_viz, // status vizzes
  'main.server.schedule': NavLocationAreas.ServerAdmin.server_schedules,
  'main.server.schedules': NavLocationAreas.ServerAdmin.server_schedules,
  'main.server.settings': NavLocationAreas.ServerAdmin.server_settings,
  'main.server.sites': NavLocationAreas.ServerAdmin.server_sites,
  'main.server.status': NavLocationAreas.ServerAdmin.server_status,
  'main.server.backgroundJobs': NavLocationAreas.ServerAdmin.server_jobs,
  'main.server.tasks': NavLocationAreas.ServerAdmin.server_tasks,
  'main.server.user': NavLocationAreas.ServerAdmin.server_users,
  'main.server.users': NavLocationAreas.ServerAdmin.server_users,

  'main.server': NavLocationAreas.ServerAdmin.server_unspecified,
};

const routerStateNameToNavLocationKeys = _.keys(routerStateNameToNavLocation);
